import type { FC } from 'react';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useParams } from 'next/navigation';
import Script from 'next/script';

import config from '@packages/config';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import type { Device } from '@packages/shared/src/utils/types';
import { type HeaderProps, Header } from '@packages/modules/src/Header/Header';
import { Usp } from '@packages/cms-components/src/modules/Usp/Usp.client';
import { useCustomerInformation } from '@packages/modules/src/hooks/useCustomerInformation/useCustomerInformation';
import { GlycerinLoginStatus } from '@packages/tracking/src/types/enums';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { ResponsiveNavigationLoaderClient } from '@packages/modules/src/Header/Navigation/ResponsiveNavigation/ResponsiveNavigationLoader.Client';

const PatchedHeader: FC<HeaderProps> = ({ isMinimal = false, device, locale }) => (
  <>
    <Script
      id="history-state-patch"
      dangerouslySetInnerHTML={{
        __html:
          'var _wr=function(t){var e=history[t];return function(){var r=e.apply(this,arguments),a=new Event(t);return a.arguments=arguments,window.dispatchEvent(a),r}};history.pushState=_wr("pushState"),history.replaceState=_wr("replaceState");',
      }}
    />
    <Header
      isMinimal={isMinimal}
      device={device}
      locale={locale}
      navigation={isMinimal ? undefined : <ResponsiveNavigationLoaderClient device={device} />}
    />
    {!isMinimal && config.usp.isEnabled && <Usp />}
  </>
);

const HeaderPage = ({ device }: { device: Device }) => {
  const params = useParams();
  const path = params ? params.path : [];
  const { language, locale } = useI18n();
  const [localeSwitch, setLocaleSwitch] = useState<boolean>(false);
  const fullHeader = [
    'mein-konto',
    'mon-compte',
    'header',
    'mijn-account',
    'passwort',
    'ruecksendeanmeldung',
  ];
  const smallHeader = ['kasse', 'smallheader'];
  const allRoutes = [...fullHeader, ...smallHeader];
  const routerPath = Array.isArray(path) && path[0] === 'fr' ? path.slice(1) : path || [];

  const dispatchGTMEvent = useTracking();
  const { isLoading, customerInformation } = useCustomerInformation();
  const { loggedIn } = useLoginState();
  const [loginStatus, setLoginStatus] = useState<GlycerinLoginStatus>();

  const getLoginStatus = useCallback(async () => {
    // eslint-disable-next-line import-x/no-extraneous-dependencies
    const { apiKeyManager } = await import('@empiriecom/mybuy-session/ApiKey');
    const apiKey = await apiKeyManager.getApiKey({ tenantId: config.tenantId });
    const loginType = apiKeyManager.getTokenContents(apiKey)?.type;

    if (loginType && loginType === 1) {
      setLoginStatus(GlycerinLoginStatus.SOFTLOGGEDIN);
      return;
    }
    if (loginType && loginType >= 1) {
      setLoginStatus(GlycerinLoginStatus.LOGGEDIN);
      return;
    }
    setLoginStatus(GlycerinLoginStatus.NOTLOGGEDIN);
  }, []);

  useEffect(() => {
    getLoginStatus().catch(() => {
      // ignore
    });
  }, [getLoginStatus, loggedIn]);

  useEffect(() => {
    if (!isLoading && loginStatus && customerInformation) {
      dispatchGTMEvent({
        event: 'dataLayerReady',
        Sprache: locale,
        LoginStatus: loginStatus,
        ...customerInformation,
        visitor: {
          ...customerInformation.visitor,
          login: loggedIn,
        },
      });
    }
    // Fix for CORE-556: searchId has been added to trigger dataLayerReady on MBA/SERP changes.
    // TODO: rework events within tracking project
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, customerInformation?.visitor.login, locale, loggedIn, loginStatus]);

  const events = ['popstate', 'pushState', 'replaceState'];
  function subscribe(callback: () => void) {
    for (const event of events) {
      window.addEventListener(event, callback);
    }
    return () => {
      for (const event of events) {
        window.removeEventListener(event, callback);
      }
    };
  }

  const syncPath = useSyncExternalStore(
    subscribe,
    () => window.location.pathname,
    () => routerPath,
  );

  // slice the path from store to remove empty first element from array: '/warenkorb' -> ['', 'warenkorb']
  const pagePath = Array.isArray(syncPath) ? syncPath : syncPath.slice(1).split('/');

  // headerRoute extension to unreadable ternary logic
  const headerRoute = (() => {
    // replacement of first ternary use pagePath[0] if valid and route exists
    if (pagePath.length > 0 && pagePath[0] && allRoutes.includes(pagePath[0])) {
      return pagePath[0];
    }

    // replacement of second ternary routerPath if available and route exists
    if (routerPath.length > 0) {
      const route = routerPath[0] === 'app' ? routerPath[1] : routerPath[0];
      if (route && allRoutes.includes(route)) {
        return route;
      }
    }
    // direct window.location.pathname parsing (does this break others?)
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const pathSegments = currentPath.slice(1).split('/');

      // Check if first segment is a language code, if so use second segment
      const firstSegment = pathSegments[0];
      const secondSegment = pathSegments[1];

      if (allRoutes.includes(firstSegment)) {
        return firstSegment;
      }

      if (secondSegment && allRoutes.includes(secondSegment)) {
        return secondSegment;
      }
    }
    return undefined;
  })();

  // does this really need to be a state? why is this declared so low in our logic chain?
  const [currentPath, setCurrentPath] = useState<string>(headerRoute ?? '');

  // reload page if locale changes to reload content in correct language
  useEffect(() => {
    if (localeSwitch) {
      window.location.reload();
    }
    setLocaleSwitch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    if (headerRoute) {
      setCurrentPath(headerRoute);
    }
    // this is a check to notify if the currentPath if where we want to go should require a reload
    if (typeof window !== 'undefined' && !allRoutes.includes(headerRoute ?? '')) {
      // dispatch our custom event
      // letting the fragments know that the route has changed and show the desired pathname
      window.dispatchEvent(
        new CustomEvent('route-change-app-header', {
          detail: { pathnames: syncPath },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerRoute]);

  // return Header if path is known
  if (fullHeader.includes(currentPath)) {
    return <PatchedHeader device={device} locale={locale} />;
  }
  // return minimal Header if path is known
  if (smallHeader.includes(currentPath)) {
    return <PatchedHeader device={device} locale={locale} isMinimal />;
  }
  // Reload page if path is unknown and hope for serverside routing
  if (typeof window !== 'undefined') {
    window.location.reload();
    return null;
  }

  // This output should never be seen. There must always be a Header!
  return (
    <>
      <Header device={device} locale={locale} />
      {config.usp.isEnabled && <Usp />}
    </>
  );
};

// eslint-disable-next-line import-x/no-default-export
export default HeaderPage;
